import type {
  WorkspaceIntroCardProps,
  WorkspaceIntroSection,
  WorkspaceIntroStep,
} from '../../../shared/ui/common/WorkspaceIntroCard';
import type {
  WorkspaceSpotlightTourDetail,
  WorkspaceSpotlightTourStep,
} from '../../../shared/ui/common/WorkspaceSpotlightTour';

interface ValidationConfigurationGuideContentArgs {
  enabledCount: number;
  totalCount: number;
}

interface ValidationConfigurationTourRefs {
  contextRef: WorkspaceSpotlightTourStep['targetRef'];
  bulkActionsRef: WorkspaceSpotlightTourStep['targetRef'];
  firstCardRef: WorkspaceSpotlightTourStep['targetRef'];
  firstToggleRef: WorkspaceSpotlightTourStep['targetRef'];
  closeActionRef: WorkspaceSpotlightTourStep['targetRef'];
}

interface ValidationConfigurationTourContentArgs {
  refs: ValidationConfigurationTourRefs;
}

const section = (title: string, bullets: string[]): WorkspaceIntroSection => ({
  title,
  bullets,
});

const step = (label: string, goal: string, checks?: string[]): WorkspaceIntroStep => ({
  label,
  goal,
  checks,
});

const detail = (
  label: string,
  bullets: string[],
  tone: WorkspaceSpotlightTourDetail['tone'] = 'default'
): WorkspaceSpotlightTourDetail => ({
  label,
  bullets,
  tone,
});

const withTarget = (
  targetRef: WorkspaceSpotlightTourStep['targetRef'],
  fallbackRect: NonNullable<WorkspaceSpotlightTourStep['fallbackRect']>,
  base: Omit<WorkspaceSpotlightTourStep, 'targetRef' | 'fallbackRect'>
): WorkspaceSpotlightTourStep => ({
  ...base,
  targetRef,
  fallbackRect,
});

export const buildValidationConfigurationGuideContent = ({
  enabledCount,
  totalCount,
}: ValidationConfigurationGuideContentArgs): WorkspaceIntroCardProps => ({
  eyebrow: 'Workspace orientation',
  title: 'Configuration decides which checks shape the queue.',
  summary:
    'Use this dialog to align the active validation checks with the review goal before you compare runs or trust the queue.',
  hint:
    'Confirm the current purpose first, use bulk actions only for broad resets, review card context before toggling, and close only when the enabled set matches the pass you are about to run.',
  status: `${enabledCount}/${totalCount} checks enabled`,
  actionLabel: 'Start tour',
  sections: [
    section('Use this when', [
      'You need the queue to answer a narrower review question than the current enabled set allows.',
      'You are comparing runs and need confidence that the same check set is active each time.',
    ]),
    section('Not for', [
      'Do not toggle rules casually in the middle of a comparison and then treat the new queue as equivalent to the last run.',
      'Do not disable a noisy rule just to make the queue smaller if the rule still belongs to the current handoff goal.',
    ]),
    section('Focus during this pass', [
      'Start with the purpose of the run, then align the enabled checks to that purpose.',
      'Use card context to understand what each rule covers before you toggle it.',
    ]),
    section('Handoff', [
      'Leave this dialog when the enabled set clearly matches the validation pass you intend to run next.',
      'Run validation again after closing so the queue reflects the updated rule set.',
    ]),
  ],
  steps: [
    step('1. Confirm the purpose of the run', 'Start by naming what the next validation pass should prove.', [
      'You know whether this is a broad readiness pass or a focused investigation.',
    ]),
    step('2. Adjust broad scope only when needed', 'Use bulk actions for resets or wide changes, not for every small tweak.', [
      'Bulk changes match the current review goal.',
    ]),
    step('3. Read the card before toggling', 'Use each validation card to understand what the rule covers and why it matters.', [
      'You are not enabling or disabling rules blindly.',
    ]),
    step('4. Close and run validation again', 'Finish here only when the enabled set matches the pass you are about to run.', [
      'The next queue should change for a clear reason you can explain.',
    ]),
  ],
});

export const buildValidationConfigurationTourSteps = ({
  refs,
}: ValidationConfigurationTourContentArgs): WorkspaceSpotlightTourStep[] => {
  const contextRect = {
    top: 94,
    left: 28,
    width: Math.max(window.innerWidth - 56, 360),
    height: 110,
  };
  const bulkActionsRect = {
    top: 214,
    left: 28,
    width: Math.max(window.innerWidth - 56, 360),
    height: 72,
  };
  const firstCardRect = {
    top: 304,
    left: 28,
    width: Math.max(window.innerWidth - 56, 360),
    height: 146,
  };
  const firstToggleRect = {
    top: 348,
    left: Math.max(window.innerWidth - 204, 36),
    width: 148,
    height: 44,
  };
  const closeRect = {
    top: Math.max(window.innerHeight - 92, 24),
    left: Math.max(window.innerWidth - 176, 24),
    width: 132,
    height: 44,
  };

  return [
    withTarget(refs.contextRef, contextRect, {
      id: 'configure-context',
      title: 'Configuration context',
      intro: 'The header tells you what this dialog is configuring and how many rules are currently enabled.',
      details: [
        detail('How to use it', [
          'Confirm the current purpose before you toggle anything.',
          'Read the enabled count as a clue about how broad or narrow the next queue will be.',
        ]),
      ],
      nextAction: 'Start here so every toggle that follows is anchored to the actual validation goal.',
    }),
    withTarget(refs.bulkActionsRef, bulkActionsRect, {
      id: 'configure-bulk-actions',
      title: 'Bulk actions',
      intro: 'Bulk actions are for wide resets and broad configuration changes across many rules.',
      details: [
        detail('How to use it', [
          'Apply broad resets when the next pass needs a clean configuration baseline.',
          'Skip bulk actions when you only need a few targeted rule changes.',
        ]),
        detail('Avoid', [
          'Do not use bulk controls to hide noise without a clear change in validation goal.',
        ], 'caution'),
      ],
      nextAction: 'Use bulk actions only when the next pass truly needs a broad rule-set change.',
    }),
    withTarget(refs.firstCardRef, firstCardRect, {
      id: 'configure-card-anatomy',
      title: 'Validation rule card',
      intro: 'Each card explains what a rule covers so you can decide whether it belongs in the next pass.',
      details: [
        detail('What to check', [
          'Use the card title and description to understand what the rule actually tests.',
          'Treat the card as the context layer that should justify the toggle state.',
        ]),
      ],
      nextAction: 'Read the card first, then decide whether this rule belongs in the next validation run.',
    }),
    withTarget(refs.firstToggleRef, firstToggleRect, {
      id: 'configure-toggle',
      title: 'Rule toggle',
      intro: 'The toggle determines whether a rule is included in the next validation run.',
      details: [
        detail('Before toggling', [
          'Know what the rule covers.',
          'Know why the next run should include or exclude it.',
        ], 'accent'),
      ],
      nextAction: 'Flip the toggle only when the rule-state change is intentional and explainable.',
    }),
    withTarget(refs.closeActionRef, closeRect, {
      id: 'configure-close',
      title: 'Close and run again',
      intro: 'Closing the dialog is the handoff point back to the next validation run.',
      details: [
        detail('How to use it', [
          'Close when the enabled set now matches the review goal.',
          'Run validation again afterward so the queue reflects the new rule set.',
        ]),
      ],
      nextAction: 'Leave the dialog only when the next queue should change for a reason you can describe clearly.',
    }),
  ];
};
